import { storedRef } from "@/components/ReferralKit";
import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Music, Gift } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { BackButton } from "@/components/BackButton";
import { isPersonalEmail } from "@/lib/schoolEmail";
import { FloatingThemeToggle } from "@/context/ThemeContext";
import { OneDeviceNotice } from "@/components/OneDeviceNotice";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const refCode = (new URLSearchParams(loc.search).get("ref") || storedRef()).toUpperCase();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(new URLSearchParams(loc.search).get("role") === "teacher" ? "teacher" : "user");
  const personalWarn = role === "teacher" && isPersonalEmail(email);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await register(name, email, password, role);
    if (res.ok && refCode) {
      try { await api.post("/referral/attribute", { code: refCode }); } catch (err) { /* non-blocking */ }
    }
    let verifySent = null;
    if (res.ok && role === "teacher") {
      try { await api.post("/teacher/verify-school/request", { school_email: email.trim() }); verifySent = email.trim(); }
      catch (err) { if (err.response?.status !== 400) verifySent = email.trim(); }
    }
    setLoading(false);
    if (res.ok && role === "teacher") nav("/teacher", { state: { verifySent } });
    else if (res.ok) nav(loc.state?.from || "/dashboard");
    else setError(res.error);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-secondary px-4 py-8">
      <FloatingThemeToggle />
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8 shadow-xl shadow-amber-900/5">
        <BackButton testId="register-back-button" />
        <Link to="/" className="mt-2 flex items-center justify-center">
          <Logo height={54} className="dark:shadow-sm" />
        </Link>
        <h1 className="mt-6 text-center font-serif text-3xl text-foreground">Start reading music</h1>
        <p className="mt-2 text-center text-sm text-muted-foreground">Create a free account to save your journey.</p>

        {refCode && (
          <div className="mt-4 flex items-center gap-2 rounded-xl border border-primary/40 bg-primary/10 px-4 py-3 text-sm text-primary" data-testid="referral-banner">
            <Gift size={16} /> You were invited with code <span className="font-mono font-semibold">{refCode}</span> — you'll pay just £450 (or £800 for 2 courses) on your first course.
          </div>
        )}

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <Label className="text-muted-foreground">I'm signing up as a…</Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setRole("user")}
                data-testid="role-student"
                className={`rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${
                  role === "user"
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-background text-muted-foreground hover:border-primary"
                }`}
              >
                Student
              </button>
              <button
                type="button"
                onClick={() => setRole("teacher")}
                data-testid="role-teacher"
                className={`rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${
                  role === "teacher"
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-background text-muted-foreground hover:border-primary"
                }`}
              >
                Teacher
              </button>
            </div>
          </div>
          <div>
            <Label htmlFor="name" className="text-muted-foreground">Name</Label>
            <Input
              id="name" required value={name} onChange={(e) => setName(e.target.value)}
              data-testid="register-name" placeholder="Your name"
              className="mt-1 border-border bg-background"
            />
          </div>
          <div>
            <Label htmlFor="email" data-testid="register-email-label" className="text-muted-foreground">
              {role === "teacher" ? "School email" : "Email"}
            </Label>
            <Input
              id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="register-email" placeholder={role === "teacher" ? "you@yourschool.org" : "you@example.com"}
              className="mt-1 border-border bg-background"
            />
            {personalWarn && (
              <p data-testid="personal-email-warning" className="mt-2 rounded-lg bg-amber-500/15 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
                This looks like a personal email. Please use your school email address so we can verify you're a teacher.
              </p>
            )}
            {role === "teacher" && (
              <p data-testid="school-email-note" className="mt-2 text-xs text-muted-foreground">
                An email will be sent to your school email address for you to verify.
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="password" className="text-muted-foreground">Password</Label>
            <Input
              id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="register-password" placeholder="At least 6 characters"
              className="mt-1 border-border bg-background"
            />
          </div>
          {error && <p data-testid="register-error" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          {role !== "teacher" && <OneDeviceNotice testId="register-one-device-notice" />}
          <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full rounded-full bg-primary py-6 text-foreground hover:bg-primary/90">
            {loading ? "Creating…" : "Create account"}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" data-testid="to-login" className="font-medium text-primary hover:underline">
            Log in
          </Link>
        </p>
        <p className="mt-3 text-center text-sm text-muted-foreground" data-testid="register-keep-in-touch">
          Not ready to sign up yet?{" "}
          <Link to="/keep-in-touch?from=register&page=/register" data-testid="register-keep-in-touch-link" className="font-medium text-primary hover:underline">Let's keep in touch</Link>
        </p>
      </div>
    </div>
  );
}
